
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Brain, Home, DollarSign, Search, Wrench } from "lucide-react";
import { useEffect } from "react";
import CanonicalTag from "@/components/CanonicalTag";
import { NeumorphicThemeProvider, useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";
import NeumorphicThemeToggle from "@/components/NeumorphicThemeToggle";
import { NeumorphicContainer } from "@/components/neumorphic";

const VisionContent = () => {
  const { theme } = useNeumorphicTheme();
  const isDark = theme === 'dark';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const mutedTextColor = isDark ? 'text-gray-300' : 'text-gray-600';
  const recessedStyles = isDark 
    ? 'bg-[#0a0a0a] shadow-[inset_8px_8px_16px_#000000,inset_-8px_-8px_16px_#141414]' 
    : 'bg-[#f0f0f0] shadow-[inset_8px_8px_16px_#d8d8d8,inset_-8px_-8px_16px_#ffffff]';

  useEffect(() => {
    // Set page title and meta description
    document.title = "Our Vision | UDA ADU";
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'UDA ADU envisions a California where every backyard becomes a long-term investment. Explore our strategic vision for the ADU revolution.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'UDA ADU envisions a California where every backyard becomes a long-term investment. Explore our strategic vision for the ADU revolution.';
      document.head.appendChild(meta);
    }

    // Add meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'UDA ADU vision, California ADU future, ADU innovation, property ROI vision');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'UDA ADU vision, California ADU future, ADU innovation, property ROI vision';
      document.head.appendChild(meta);
    }

    // Add canonical URL
    const canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    canonicalLink.href = 'https://www.udaadu.com/vision';
    document.head.appendChild(canonicalLink);

    // Add JSON-LD structured data
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Our Vision - UDA ADU",
      "description": "UDA ADU's vision for transforming backyards into intelligent property investments through design, strategy, and ROI tools.",
      "url": "https://www.udaadu.com/vision"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    // Cleanup function to remove added elements when component unmounts
    return () => {
      const existingCanonical = document.querySelector('link[rel="canonical"][href="https://www.udaadu.com/vision"]');
      if (existingCanonical) {
        existingCanonical.remove();
      }
      
      const existingJsonLd = document.querySelector('script[type="application/ld+json"]');
      if (existingJsonLd && existingJsonLd.textContent?.includes('"url": "https://www.udaadu.com/vision"')) {
        existingJsonLd.remove();
      }
    };
  }, []);

  return (
    <NeumorphicContainer className="min-h-screen">
      <CanonicalTag url="https://www.udaadu.com/vision" />
      <Navigation />
      <NeumorphicThemeToggle />
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Main Content in Recessed Container */}
        <div className={`rounded-2xl p-8 md:p-12 transition-all duration-300 ${recessedStyles}`}>
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-[#e4683b] mb-6">
              Our Vision: A New Era of Property Intelligence
            </h1>
            <p className={`text-xl md:text-2xl ${mutedTextColor} font-light mb-8`}>
              We see every backyard as a balance sheet.
            </p>
          </div>

          {/* Main Content */}
          <div className="mb-16">
            <p className={`text-lg ${mutedTextColor} leading-relaxed mb-12 text-center max-w-3xl mx-auto`}>
              In the future we're building, every homeowner becomes a portfolio manager. Our vision is to turn overlooked yards into optimized, permitted, income-generating opportunities. We lead with design, but we speak in spreadsheets. Our future is not about more buildings — it's about better decisions.
            </p>
          </div>

          {/* Highlighted Values Section */}
          <div className="mb-16">
            <div className="grid gap-6 max-w-2xl mx-auto">
              {[
                {
                  icon: Brain,
                  text: "Data-backed ADU planning"
                },
                {
                  icon: Home,
                  text: "Community-conscious design"
                },
                {
                  icon: DollarSign,
                  text: "Financial transparency + equity modeling"
                },
                {
                  icon: Search,
                  text: "AI tools to assess feasibility and ROI in real time"
                },
                {
                  icon: Wrench,
                  text: "Permit-ready blueprints for faster approvals"
                }
              ].map((value, index) => (
                <div key={index} className="flex items-start gap-4 p-4">
                  <value.icon className="h-6 w-6 text-[#e4683b] flex-shrink-0 mt-0.5" />
                  <p className={mutedTextColor + " text-lg"}>{value.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Quote Block */}
          <div className="text-center mb-16">
            <blockquote className="text-2xl md:text-3xl font-light text-[#e4683b] italic">
              "An ADU is no longer just a guesthouse. It's a gateway to long-term wealth."
            </blockquote>
          </div>

          {/* Call to Action Section */}
          <div className="text-center">
            <h2 className={`text-2xl md:text-3xl font-bold ${textColor} mb-8`}>
              Let us show you how the future of property starts at the back fence.
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/contact-us" className={`px-8 py-3 text-lg rounded-xl font-medium transition-all duration-300 ${
                isDark
                  ? 'bg-[#0a0a0a] text-[#e4683b] shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414] hover:shadow-[10px_10px_20px_#000000,-10px_-10px_20px_#141414]'
                  : 'bg-white text-[#e4683b] shadow-[8px_8px_16px_#d8d8d8,-8px_-8px_16px_#ffffff] hover:shadow-[10px_10px_20px_#d0d0d0,-10px_-10px_20px_#ffffff]'
              }`}>Talk to a Strategist</Link>
              <Link to="/our-work" className={`px-8 py-3 text-lg rounded-xl font-medium transition-all duration-300 ${
                isDark
                  ? 'bg-[#0a0a0a] text-[#e4683b] shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414] hover:shadow-[10px_10px_20px_#000000,-10px_-10px_20px_#141414]'
                  : 'bg-white text-[#e4683b] shadow-[8px_8px_16px_#d8d8d8,-8px_-8px_16px_#ffffff] hover:shadow-[10px_10px_20px_#d0d0d0,-10px_-10px_20px_#ffffff]'
              }`}>View Our Portfolio</Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </NeumorphicContainer>
  );
};

const Vision = () => {
  return (
    <NeumorphicThemeProvider>
      <VisionContent />
    </NeumorphicThemeProvider>
  );
};

export default Vision;
